import React from "react";
import { useObserver } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { Grid, Typography } from "@material-ui/core";
import { useAuthStore } from "stores/AuthStore";
import { Link, Dialog, Table } from "components";
import { StoreProvider, useStore } from "./Store";
import { FormStoreProvider, useFormStore } from "./FormStore";
import Form from "./Form";

interface Params {
  agencyId: string;
}

export const Page = () => {
  const authStore = useAuthStore();
  const store = useStore();
  const formStore = useFormStore();
  const params = useParams<Params>();

  React.useEffect(() => {
    store.fetchAppGroups(+params.agencyId);
  }, [store, params.agencyId]);

  const open = React.useCallback(() => formStore.modal(true), [formStore]);
  const close = React.useCallback(() => formStore.modal(false), [formStore]);

  const data = store.appGroups.map((appGroup) => ({
    ...appGroup,
    ios: appGroup.apps.find((a) => a.os === "ios"),
    android: appGroup.apps.find((a) => a.os === "android"),
    trackingVendor: appGroup.apps[0]?.trackingVendor,
    redUserId: appGroup.apps[0]?.redUserId,
    isTver: appGroup.apps[0]?.isTver,
    twitterUserId: appGroup.apps[0]?.twitterUserId,
  }));

  return useObserver(() => (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs="auto">
        <Typography variant="h6" color="textSecondary">
          アプリ
        </Typography>
      </Grid>

      {authStore.admin() && (
        <Grid item xs="auto">
          <Dialog
            title="アプリを追加"
            buttonText="アプリを追加"
            open={formStore.open}
            onOpen={open}
            onClose={close}
          >
            <Form agencyId={+params.agencyId} />
          </Dialog>
        </Grid>
      )}

      <Grid item xs={12}>
        <Table
          title=""
          isLoading={store.loading}
          options={{
            exportFileName: "アプリ",
          }}
          data={data}
          columns={[
            { title: "名前", field: "name" },
            { title: "計測ベンダー", field: "trackingVendor.name" },
            {
              title: "iOS App ID",
              field: "ios.appId",
              render: (appGroup) =>
                appGroup.ios && (
                  <Link
                    to={`/agencies/${params.agencyId}/apps/${appGroup.ios.id}`}
                  >
                    {appGroup.ios.appId}
                  </Link>
                ),
            },
            {
              title: "Android App ID",
              field: "android.appId",
              render: (appGroup) =>
                appGroup.android && (
                  <Link
                    to={`/agencies/${params.agencyId}/apps/${appGroup.android.id}`}
                  >
                    {appGroup.android.appId}
                  </Link>
                ),
            },
            { title: "Red 広告主 ID", field: "redUserId" },
            { title: "Twitter アカウント ID", field: "twitterUserId" },
          ]}
          actions={[
            {
              hidden: !authStore.admin(),
              icon: "edit",
              tooltip: "編集",
              onClick: (_, appGroup) =>
                !Array.isArray(appGroup) &&
                formStore.modal(true, {
                  trackingVendorId: appGroup.trackingVendor.id,
                  appGroupId: appGroup.id,
                  ios: {
                    id: appGroup.ios?.id,
                    redDmpAppId: appGroup.ios?.redDmpAppId,
                  },
                  android: {
                    id: appGroup.android?.id,
                    redDmpAppId: appGroup.android?.redDmpAppId,
                  },
                  redUserId: appGroup.redUserId,
                  isTver: appGroup.isTver,
                  twitterUserId: appGroup.twitterUserId,
                }),
            },
          ]}
        />
      </Grid>
    </Grid>
  ));
};

export default () => (
  <StoreProvider>
    <FormStoreProvider>
      <Page />
    </FormStoreProvider>
  </StoreProvider>
);
